import { FluentBundle } from 'fluent';
import {
  GetString,
} from 'fluent-react';
import React from 'react';
import styled from 'styled-components';
import { LookBackYearRange } from '../../graphQL/types/shared';
import {
  Product,
  TradeFlow,
 } from '../../graphQL/types/shared';
import PresentationManager from '../../presentationManager/PresentationManager';
import CountrySelector from '../countrySelector/InteriorCountrySelector';
import LeftPanelBoxShadow from '../exportBasket/LeftPanelBoxShadow';
import ComplexityLegend from '../exportComplexityLegend';
import GraphControlDropdown from '../graphControlDropdown';
import {
  IOption,
} from '../graphControlDropdown';
import {
  Root,
} from '../Grid';
import {
  GraphControlsContainer,
} from '../Grid';
import HamburgerMenu from '../menu';
import SectionNav from '../sectionNav';
import Sidebar from '../sidebar';
import TreeMap from '../treeMap';
import UtilityBar from '../utilityBarItems';
import {chapters} from '../utilityBarItems/VideoTutorial';
import {
  CommonElementName,
  defaultFadeTransition,
  defaultVerticalSlideTransition,
  getUniqueSlideElementKey,
  SlideName,
  TreeMapColorCriterion,
} from '../Utils';
import SectorLabels from './SectorLabels';
import StatusBar from './StatusBar';
import {
  boxLabelSize,
  boxSize,
  gridColumnSpacing,
} from './StatusBar';
import TopOfPageIndicators from './TopOfPageIndicators';

//#region Styling
export const ComplexitySelectorContainer = styled(GraphControlsContainer)`
  display: flex;
  justify-content: flex-end;
  margin-right: ${boxSize + boxLabelSize + (gridColumnSpacing * 3)}px;
`;
//#endregion

const getTreMapColorCriterionOptions =
  (getFluentString: GetString): Array<IOption<TreeMapColorCriterion>> => ([
    {
      value: TreeMapColorCriterion.Sector,
      label: getFluentString('tree-map-color-criterion-selector-sector'),
      isEnabled: true,
    },
    {
      value: TreeMapColorCriterion.Complexity,
      label: getFluentString('tree-map-color-criterion-selector-complexity'),
      isEnabled: true,
    },
  ]);

export type NewProduct = {
  id: Product['id'];
  topLevelParent: {
    id: Product['id'];
    shortName: Product['shortName'];
    productType: Product['productType'];
  }
};

interface Input {
  getFluentString: GetString;
  countryId: string;
  year: number;
  selectCountry: (country: string) => void;
  slideIndex: number;
  firstContentfulSlideIndex: number;
  setSlideIndex: (index: number) => void;
  goToNextSlide: () => void;
  goToPrevSlide: () => void;
  fluentBundle: FluentBundle;
  countrySelectorZIndex: number;
  sectionNavZIndex: number;
  sidebarZIndex: number;
  hamburgerMenuZIndex: number;
  sectorLabelsZIndex: number;
  topOfPageIndicatorsZIndex: number;
  chartZIndex: number;
  newProductsLegendZIndex: number;
  tradeFlow: TradeFlow;
  latestYear: number;
  helpTipsTriggerZIndex: number;
  toggleHelpTooltips: () => void;
  helpTooltipsActive: boolean;
  colorCriterion: TreeMapColorCriterion;
  setColorCriterion: (value: TreeMapColorCriterion) => void;
  colorCriterionDropdownZIndex: number;
  promotedZIndex: number;
}

const newProductsSlide = (input: Input) => {
  const {
    countryId, selectCountry, year,
    firstContentfulSlideIndex, slideIndex, setSlideIndex,
    goToNextSlide, goToPrevSlide, fluentBundle, countrySelectorZIndex,
    sectionNavZIndex, sidebarZIndex,
    sectorLabelsZIndex,
    hamburgerMenuZIndex, getFluentString, topOfPageIndicatorsZIndex,
    chartZIndex, tradeFlow, latestYear,
    newProductsLegendZIndex, toggleHelpTooltips, helpTooltipsActive,
    helpTipsTriggerZIndex, colorCriterion, setColorCriterion,
    colorCriterionDropdownZIndex, promotedZIndex,
  } = input;

  const helpTooltipOrder = {
    dropdown: 1,
    sectionNav: 2,
    pageAdvancer: 3,
    sectorLabels: 4,
  };

  const legend = colorCriterion === TreeMapColorCriterion.Sector
    ? (
      <SectorLabels
        countryId={countryId}
        year={latestYear}
        zIndex={sectorLabelsZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.SectorLabels}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.sectorLabels}
        getFluentString={getFluentString}
      />
      )
    : (
      <ComplexityLegend
        countryId={countryId}
        year={latestYear}
        getFluentString={getFluentString}
        showEci={false}
        zIndex={sectorLabelsZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.NewProducts, 'graph-legend')}
      />
      );

  return (
    <PresentationManager RootComponent={Root} slideIndex={slideIndex}>
      <CountrySelector
        selectedCountry={countryId}
        onCountrySelect={selectCountry}
        promptText={getFluentString('country-selector-prompt')}
        getFluentString={getFluentString}
        zIndex={countrySelectorZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.CountrySelector}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.dropdown}
      />
      <SectionNav
        startingIndexOfFirstSection={0}
        slideIndex={slideIndex}
        setSlideIndex={setSlideIndex}
        year={year}
        getFluentString={getFluentString}
        countryId={countryId}
        zIndex={sectionNavZIndex}
        key={CommonElementName.SectionNav}
        transition={defaultVerticalSlideTransition}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.sectionNav}
      />
      <Sidebar
        firstContentfulSlideIndex={firstContentfulSlideIndex}
        slideIndex={slideIndex}
        getFluentString={getFluentString}
        goToNextSlide={goToNextSlide}
        goToPrevSlide={goToPrevSlide}
        countryId={countryId}
        fluentBundle={fluentBundle}
        yearRange={LookBackYearRange.FifteenYears}
        zIndex={sidebarZIndex}
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.Sidebar}
        isHelpActive={helpTooltipsActive}
        helpTooltipIndex={helpTooltipOrder.pageAdvancer}
      />
      <LeftPanelBoxShadow
        transition={defaultVerticalSlideTransition}
        key={CommonElementName.LeftPanelBackground}
      />
      <StatusBar
        countryId={countryId}
        year={latestYear}
        getFluentString={getFluentString}
        zIndex={newProductsLegendZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.NewProducts, 'status-bar')}
      />
      <GraphControlDropdown
        label={getFluentString('tree-map-color-criterion-selector-label')}
        selectedValue={colorCriterion}
        options={getTreMapColorCriterionOptions(getFluentString)}
        onValueSelect={setColorCriterion}
        zIndex={colorCriterionDropdownZIndex}
        Container={ComplexitySelectorContainer}
        promotedZIndex={promotedZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.ExportBasketExportComplexityColorSelector}
      />
      <TreeMap
        tradeFlow={tradeFlow}
        countryId={countryId}
        colorCriterion={colorCriterion}
        getFluentString={getFluentString}
        year={latestYear}
        onlyShowNewProducts={true}
        zIndex={chartZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.NewProducts, 'NewProductsTreeMap')}
      />
      <HamburgerMenu
        getFluentString={getFluentString}
        zIndex={hamburgerMenuZIndex}
        transition={defaultFadeTransition}
        key={CommonElementName.HamburgerMenu}
      />
      <UtilityBar
        getFluentString={getFluentString}
        zIndex={helpTipsTriggerZIndex}
        toggleHelpTooltips={toggleHelpTooltips}
        helpTooltipsActive={helpTooltipsActive}
        transition={defaultFadeTransition}
        countryId={countryId}
        videoChapter={chapters[6]}
        key={CommonElementName.HelpTipsTrigger}
      />
      <TopOfPageIndicators
        countryId={countryId}
        getFluentString={getFluentString}
        zIndex={topOfPageIndicatorsZIndex}
        transition={defaultFadeTransition}
        key={getUniqueSlideElementKey(SlideName.NewProducts, 'top-page-indicators')}
      />
      {legend}
    </PresentationManager>
  );
};

export default newProductsSlide;
